/**
 * Vite plugin for automatic SMRT service generation
 * Provides virtual modules for REST, MCP, and other services
 */

import { existsSync, readFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import type {
  DomainKnowledgeConfig,
  DomainKnowledgeManifest,
} from '@happyvertical/smrt-types';
import type { Plugin, ResolvedConfig, ViteDevServer } from 'vite';
import { buildDomainKnowledgeManifest } from '../knowledge.js';
import { discoverSmrtPackages } from '../manifest/discover-smrt-packages.js';
import type { SmartObjectManifest } from '../scanner/types';
import type { SchemaDefinition } from '../schema/types.js';
import { importWorkspaceModule } from '../utils/import-workspace-module.js';
import type { ScannerModule } from '../utils/scanner-module.js';
import { importBuildAwareModule } from './import-build-aware.js';
import {
  findCliApiCoherenceViolations,
  generateSvelteKitRoutes,
  methodNameToKebab,
  resolveApiActionSet,
  validateCliIncludeAgainstApi,
} from './sveltekit-generator.js';

export type {
  CliApiCoherenceViolation,
  SvelteKitOptions,
} from './sveltekit-generator.js';
// Re-export SvelteKit route generator for CLI usage
export {
  findCliApiCoherenceViolations,
  generateSvelteKitRoutes,
  methodNameToKebab,
  resolveApiActionSet,
  validateCliIncludeAgainstApi,
} from './sveltekit-generator.js';

/**
 * Minimal structural shape of a parsed `package.json`. Only the fields the
 * plugin reads are typed; the index signature keeps the rest accessible (e.g.
 * `exports`, consumed downstream by agent-manifest generation).
 */
interface PackageJsonShape {
  name?: string;
  version?: string;
  [key: string]: unknown;
}

export interface SmrtPluginOptions {
  /** Glob patterns for SMRT source files */
  include?: string[];
  /** Patterns to exclude */
  exclude?: string[];
  /** Output directory for generated files */
  outDir?: string;
  /** Enable hot module replacement */
  hmr?: boolean;
  /** Watch for file changes */
  watch?: boolean;
  /** Generate types */
  generateTypes?: boolean;
  /** Custom base classes to scan for */
  baseClasses?: string[];
  /** Follow import chains to detect inheritance (e.g., Event extends SmrtObject) */
  followImports?: boolean;
  /** Directory to write TypeScript declarations (relative to project root) */
  typeDeclarationsPath?: string;
  /** Plugin execution mode - controls Node.js vs browser compatibility */
  mode?: 'server' | 'client' | 'auto';
  /**
   * @deprecated OXC scanner is now the only scanner. This option is ignored.
   */
  experimentalFastScanner?: boolean;
  /** SvelteKit route auto-generation options */
  svelteKit?: {
    /** Enable SvelteKit route generation (default: false) */
    enabled: boolean;
    /** Output directory for generated routes (default: 'src/routes/api') */
    routesDir?: string;
    /** Directory containing SMRT objects (default: 'src/lib/objects') */
    objectsDir?: string;
    /** Directory for configuration file (default: 'src/lib/server') */
    configPath?: string;
    /** Configuration file name (default: 'smrt.ts') */
    configFileName?: string;
    /**
     * Apply kebab-case to custom-method URL segments (e.g. `discoverFromUrl`
     * becomes `/discover-from-url`). Opt-in for one minor; default flips in
     * the next major. An explicit `api.routes[name].path` always wins.
     */
    kebabRoutes?: boolean;
  };
  /** Domain-scoped agent/developer knowledge artifact generation. */
  knowledge?: DomainKnowledgeConfig | false;
  /**
   * Validate that every method in `cli.include` is exposed via the API
   * (so HTTP-based CLI consumers can actually reach them). Default: true.
   * Per-class opt-out via `cli: { skipApiCheck: true }`.
   */
  validateCliApiCoherence?: boolean;
}

const VIRTUAL_MODULES = {
  '@happyvertical/smrt-virt-routes': 'smrt:routes',
  '@happyvertical/smrt-virt-client': 'smrt:client',
  '@happyvertical/smrt-virt-mcp': 'smrt:mcp',
  '@happyvertical/smrt-virt-types': 'smrt:types',
  '@happyvertical/smrt-virt-manifest': 'smrt:manifest',
  '@happyvertical/smrt-virt-schema': 'smrt:schema',
  '@happyvertical/smrt-virt-ui': 'smrt:ui',
  '@happyvertical/smrt-virt-cli': 'smrt:cli',
};

async function importScanner() {
  return importWorkspaceModule<ScannerModule>({
    packageName: '@happyvertical/smrt-scanner',
    distEntry: 'packages/scanner/dist/index.js',
    sourceEntry: 'packages/scanner/src/index.ts',
    purpose: 'Vite manifest scanning',
  });
}

export function smrtPlugin(options: SmrtPluginOptions = {}): Plugin {
  const {
    include = ['src/**/*.ts', 'src/**/*.js'],
    exclude = ['**/*.test.ts', '**/*.spec.ts', '**/node_modules/**'],
    hmr = true,
    watch = true,
    generateTypes = true,
    baseClasses = ['SmrtObject', 'SmartObject'],
    followImports = true, // Default true: needed for multi-package inheritance
    typeDeclarationsPath = 'src/types',
    mode = 'auto',
    svelteKit = {
      enabled: false,
      routesDir: 'src/routes/api',
      objectsDir: 'src/lib/objects',
      configPath: 'src/lib/server',
      configFileName: 'smrt.ts',
    },
    knowledge,
    validateCliApiCoherence = true,
  } = options;

  /**
   * Dev-watcher variant of the cli↔api coherence lint: warn instead of throw,
   * so an in-flight decorator edit doesn't kill the dev server. Uses the same
   * remediation text as the strict build-time gate (`validateCliIncludeAgainstApi`)
   * so the dev-server message is as actionable as the build failure.
   */
  function warnCliApiCoherenceViolations(m: SmartObjectManifest): void {
    if (!validateCliApiCoherence) return;
    const violations = findCliApiCoherenceViolations(m);
    if (violations.length === 0) return;
    for (const { className, unreachable } of violations) {
      for (const action of unreachable) {
        console.warn(
          `[smrt] ${className}.${action} is declared in cli.include but is not ` +
            `exposed via the api. Build will fail until this is resolved.\n` +
            `  Either:\n` +
            `    - Add '${action}' to api.include, or\n` +
            `    - Remove '${action}' from cli.include.\n` +
            `  The CLI invokes methods over HTTP; methods without API routes are unreachable.\n` +
            `  If this CLI is intentionally invoked in-process (no HTTP), set\n` +
            `  \`cli: { skipApiCheck: true }\` on the @smrt() decorator to acknowledge.`,
        );
      }
    }
  }

  let server: ViteDevServer | undefined;
  let manifest: SmartObjectManifest | null = null;
  let pluginMode: 'server' | 'client' = 'server';
  let projectRoot: string = process.cwd();
  let config: ResolvedConfig | null = null; // Store resolved config for closeBundle hook
  let resolvedPluginNames: string[] = [];

  /**
   * Write manifest to .smrt/manifest.json for CLI discovery.
   * This ensures `smrt db:migrate`, `smrt db:status`, etc. can find
   * locally-defined SMRT objects in non-library builds (Issue #963).
   */
  async function writeLocalManifest(
    m: SmartObjectManifest,
    rootDir: string,
  ): Promise<void> {
    try {
      const { writeFileSync, mkdirSync } = await import('node:fs');
      const { resolve } = await import('node:path');

      const smrtDir = resolve(rootDir, '.smrt');
      mkdirSync(smrtDir, { recursive: true });

      const manifestPath = resolve(smrtDir, 'manifest.json');
      writeFileSync(manifestPath, JSON.stringify(m, null, 2), 'utf-8');
      await writeDomainKnowledgeArtifact(
        m,
        rootDir,
        resolve(smrtDir, 'smrt-knowledge.json'),
        manifestPath,
      );

      const objectCount = Object.keys(m.objects).length;
      console.log(
        `[smrt] Wrote local manifest with ${objectCount} objects to .smrt/manifest.json`,
      );
    } catch (error) {
      console.error('[smrt] Error writing local manifest:', error);
    }
  }

  async function writeDomainKnowledgeArtifact(
    m: SmartObjectManifest,
    rootDir: string,
    outputPath: string,
    manifestPath: string,
  ): Promise<void> {
    const resolvedKnowledge = await resolveKnowledgeConfig(rootDir, m);
    if (resolvedKnowledge.enabled === false) {
      return;
    }

    const { mkdirSync, writeFileSync } = await import('node:fs');
    const { dirname } = await import('node:path');
    mkdirSync(dirname(outputPath), { recursive: true });
    const artifact = buildDomainKnowledgeManifest({
      manifest: m,
      rootDir,
      manifestPath,
      config: resolvedKnowledge,
    });
    writeFileSync(
      outputPath,
      JSON.stringify(
        preserveKnowledgeGeneratedAt(outputPath, artifact),
        null,
        2,
      ),
      'utf-8',
    );
  }

  async function resolveKnowledgeConfig(
    rootDir: string,
    m: SmartObjectManifest,
  ): Promise<DomainKnowledgeConfig> {
    if (knowledge === false) return { enabled: false };
    const packageName = m.packageName ?? readPackageName(rootDir);
    const defaults: DomainKnowledgeConfig = {
      enabled: true,
      api: {
        enabled: false,
        basePath: '/__smrt/knowledge',
        requireAdmin: true,
        includeDocs: false,
        includePrompts: false,
      },
      includeDocs: true,
      includePrompts: true,
    };

    let fileKnowledge: DomainKnowledgeConfig = {};
    let packageKnowledge: DomainKnowledgeConfig = {};
    try {
      const previousCwd = process.cwd();
      process.chdir(rootDir);
      try {
        const { loadConfig } = await import('@happyvertical/smrt-config');
        const config = await loadConfig({ cache: false });
        fileKnowledge = (config.knowledge ?? {}) as DomainKnowledgeConfig;
        packageKnowledge = (
          packageName ? (config.packages?.[packageName]?.knowledge ?? {}) : {}
        ) as DomainKnowledgeConfig;
      } finally {
        process.chdir(previousCwd);
      }
    } catch {
      fileKnowledge = {};
      packageKnowledge = {};
    }

    return mergeKnowledgeConfig(
      defaults,
      fileKnowledge,
      packageKnowledge,
      knowledge || {},
    );
  }

  function mergeKnowledgeConfig(
    ...configs: Array<DomainKnowledgeConfig | undefined | null | false>
  ): DomainKnowledgeConfig {
    const merged: DomainKnowledgeConfig = {};
    for (const next of configs) {
      if (!next) continue;
      const hasApi = Boolean(merged.api || next.api);
      const api = hasApi
        ? { ...(merged.api ?? {}), ...(next.api ?? {}) }
        : undefined;
      Object.assign(merged, next);
      if (api) merged.api = api;
    }
    return merged;
  }

  function preserveKnowledgeGeneratedAt(
    outputPath: string,
    nextKnowledge: DomainKnowledgeManifest,
  ): DomainKnowledgeManifest {
    if (!existsSync(outputPath)) return nextKnowledge;

    try {
      const current = JSON.parse(
        readFileSync(outputPath, 'utf8'),
      ) as Partial<DomainKnowledgeManifest>;
      if (
        semanticKnowledgeJson(current) === semanticKnowledgeJson(nextKnowledge)
      ) {
        return {
          ...nextKnowledge,
          generatedAt: current.generatedAt ?? nextKnowledge.generatedAt,
        };
      }
    } catch {
      // Replace malformed artifacts.
    }

    return nextKnowledge;
  }

  function semanticKnowledgeJson(
    artifact: Partial<DomainKnowledgeManifest>,
  ): string {
    const { generatedAt: _generatedAt, ...rest } = artifact ?? {};
    return JSON.stringify(sortJson(rest));
  }

  function sortJson(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(sortJson);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, entry]) => [key, sortJson(entry)]),
      );
    }
    return value;
  }

  function readPackageName(rootDir: string): string | undefined {
    try {
      const pkg = JSON.parse(
        readFileSync(join(rootDir, 'package.json'), 'utf8'),
      );
      return typeof pkg.name === 'string' ? pkg.name : undefined;
    } catch {
      return undefined;
    }
  }

  function validateConsumerPluginSetup(
    m: SmartObjectManifest,
    context: 'configResolved' | 'buildStart',
  ): void {
    if (config?.mode === 'test') {
      return;
    }

    if (config?.build?.lib) {
      return;
    }

    const consumerDependencies = (m.smrtDependencies || []).filter(
      (dependency) => dependency !== '@happyvertical/smrt-core',
    );
    if (consumerDependencies.length === 0) {
      return;
    }

    const hasConsumerPlugin = resolvedPluginNames.some(
      (name) => name === 'smrt-consumer',
    );
    if (hasConsumerPlugin) {
      return;
    }

    const dependencySummary =
      consumerDependencies.length <= 4
        ? consumerDependencies.join(', ')
        : `${consumerDependencies.slice(0, 4).join(', ')}, +${consumerDependencies.length - 4} more`;

    throw new Error(
      `[smrt] Consumer project misconfiguration detected during ${context}: found external SMRT dependencies (${dependencySummary}) but vite.config is missing smrtConsumer(). Add "import { smrtConsumer } from '@happyvertical/smrt-core/consumer-plugin'" and register it alongside smrtPlugin() so .smrt/register.js is generated for CLI/runtime class loading.`,
    );
  }

  function validateLibraryMinifySetup(
    m: SmartObjectManifest,
    context: 'configResolved' | 'buildStart',
  ): void {
    if (!config?.build?.lib) {
      return;
    }

    if (Object.keys(m.objects || {}).length === 0) {
      return;
    }

    if (config.build.minify === false) {
      return;
    }

    throw new Error(
      `[smrt] Library package misconfiguration detected during ${context}: packages that publish SMRT objects must set Vite build.minify = false. Minification rewrites runtime class names and breaks manifest registration, STI resolution, and collection queries in downstream apps.`,
    );
  }

  return {
    name: 'smrt-auto-service',

    // Expose options for external access (e.g., test manifest generation)
    api: {
      options: {
        baseClasses,
        followImports,
        include,
        exclude,
      },
    },

    async configResolved(resolvedConfig) {
      // Store config for closeBundle hook
      config = resolvedConfig;
      resolvedPluginNames = (resolvedConfig.plugins || []).map(
        (plugin) => plugin?.name || '',
      );

      // Store project root for file scanning
      projectRoot = resolvedConfig.root;

      // Detect plugin mode based on build configuration
      if (mode === 'auto') {
        const isSSRBuild = resolvedConfig.build?.ssr;
        const isFederationBuild = resolvedConfig.plugins.some((p) =>
          p.name?.includes('federation'),
        );
        const isClientBuild =
          isFederationBuild ||
          (!isSSRBuild && resolvedConfig.build?.target === 'esnext');

        pluginMode = isClientBuild ? 'client' : 'server';
      } else {
        pluginMode = mode;
      }

      console.log(`[smrt] Running in ${pluginMode} mode`);

      // Scan files and generate initial manifest in all modes
      manifest = await scanAndGenerateManifest(projectRoot);

      // Write local manifest for CLI discovery (Issue #963)
      if (manifest) {
        await writeLocalManifest(manifest, projectRoot);
        validateLibraryMinifySetup(manifest, 'configResolved');
        validateConsumerPluginSetup(manifest, 'configResolved');
        if (validateCliApiCoherence) {
          validateCliIncludeAgainstApi(manifest);
        }
      }

      // Generate SvelteKit routes if enabled
      if (svelteKit.enabled && manifest) {
        await generateSvelteKitRoutes(resolvedConfig.root, manifest, {
          enabled: svelteKit.enabled,
          routesDir: svelteKit.routesDir || 'src/routes/api',
          objectsDir: svelteKit.objectsDir || 'src/lib/objects',
          configPath: svelteKit.configPath || 'src/lib/server',
          configFileName: svelteKit.configFileName || 'smrt.ts',
          kebabRoutes: svelteKit.kebabRoutes ?? false,
          knowledge: await resolveKnowledgeConfig(
            resolvedConfig.root,
            manifest,
          ),
        });
      }
    },

    async buildStart() {
      // Rescan files on build start in all modes
      manifest = await scanAndGenerateManifest(projectRoot);

      // Write local manifest for CLI discovery (Issue #963)
      if (manifest) {
        await writeLocalManifest(manifest, projectRoot);
        validateLibraryMinifySetup(manifest, 'buildStart');
        validateConsumerPluginSetup(manifest, 'buildStart');
        if (validateCliApiCoherence) {
          validateCliIncludeAgainstApi(manifest);
        }
      }
    },

    configureServer(devServer) {
      server = devServer;

      // Serve default HTML when no index.html exists
      // Skip for SvelteKit projects (they use src/app.html and handle routing themselves)
      devServer.middlewares.use(async (req, res, next) => {
        // Skip default UI entirely for SvelteKit projects
        if (svelteKit.enabled) {
          return next();
        }

        if (req.url === '/' || req.url === '/index.html') {
          try {
            const { existsSync } = await import('node:fs');
            const { join } = await import('node:path');

            const projectRoot = devServer.config.root;
            const indexPath = join(projectRoot, 'index.html');

            // If index.html exists, let Vite handle it
            if (existsSync(indexPath)) {
              return next();
            }

            // Otherwise, serve default SMRT UI
            console.log('[smrt] Serving default UI (no index.html found)');
            let html = getDefaultHTML();

            // Apply Vite's HTML transformation to process module imports
            html = await devServer.transformIndexHtml('/', html);

            res.setHeader('Content-Type', 'text/html');
            res.end(html);
            return;
          } catch (error) {
            console.error('[smrt] Error serving default HTML:', error);
            return next();
          }
        }
        next();
      });

      // Set up file watching in all modes when enabled
      if (watch && hmr) {
        // Watch for file changes
        const watcher = devServer.watcher;

        // Guard each watcher handler: scanAndGenerateManifest re-throws on a
        // mid-edit scan error, and an unhandled rejection in a watcher callback
        // can crash the dev server. Log and keep the previous manifest (#1378).
        watcher.on('change', async (file) => {
          try {
            if (await shouldRescan(file)) {
              console.log(`[smrt] Rescanning due to change in ${file}`);
              manifest = await scanAndGenerateManifest(projectRoot);

              // Write local manifest for CLI discovery (Issue #963)
              if (manifest) {
                await writeLocalManifest(manifest, projectRoot);
                warnCliApiCoherenceViolations(manifest);
              }

              // Generate SvelteKit routes if enabled
              if (svelteKit.enabled && manifest && server) {
                await generateSvelteKitRoutes(server.config.root, manifest, {
                  enabled: svelteKit.enabled,
                  routesDir: svelteKit.routesDir || 'src/routes/api',
                  objectsDir: svelteKit.objectsDir || 'src/lib/objects',
                  configPath: svelteKit.configPath || 'src/lib/server',
                  configFileName: svelteKit.configFileName || 'smrt.ts',
                  kebabRoutes: svelteKit.kebabRoutes ?? false,
                  knowledge: await resolveKnowledgeConfig(
                    server.config.root,
                    manifest,
                  ),
                });
              }

              // Invalidate virtual modules
              Object.values(VIRTUAL_MODULES).forEach((id) => {
                const module = server?.moduleGraph.getModuleById(id);
                if (module) {
                  server?.reloadModule(module);
                }
              });
            }
          } catch (error) {
            console.error(
              `[smrt] Failed to rescan after change in ${file}:`,
              error,
            );
          }
        });

        watcher.on('add', async (file) => {
          try {
            if (await shouldRescan(file)) {
              console.log(`[smrt] Rescanning due to new file ${file}`);
              manifest = await scanAndGenerateManifest(projectRoot);

              // Write local manifest for CLI discovery (Issue #963)
              if (manifest) {
                await writeLocalManifest(manifest, projectRoot);
                warnCliApiCoherenceViolations(manifest);
              }

              // Generate SvelteKit routes if enabled
              if (svelteKit.enabled && manifest && server) {
                await generateSvelteKitRoutes(server.config.root, manifest, {
                  enabled: svelteKit.enabled,
                  routesDir: svelteKit.routesDir || 'src/routes/api',
                  objectsDir: svelteKit.objectsDir || 'src/lib/objects',
                  configPath: svelteKit.configPath || 'src/lib/server',
                  configFileName: svelteKit.configFileName || 'smrt.ts',
                  kebabRoutes: svelteKit.kebabRoutes ?? false,
                  knowledge: await resolveKnowledgeConfig(
                    server.config.root,
                    manifest,
                  ),
                });
              }
            }
          } catch (error) {
            console.error(
              `[smrt] Failed to rescan after adding ${file}:`,
              error,
            );
          }
        });

        watcher.on('unlink', async (file) => {
          try {
            if (await shouldRescan(file)) {
              console.log(`[smrt] Rescanning due to removed file ${file}`);
              manifest = await scanAndGenerateManifest(projectRoot);

              // Write local manifest for CLI discovery (Issue #963)
              if (manifest) {
                await writeLocalManifest(manifest, projectRoot);
                warnCliApiCoherenceViolations(manifest);
              }

              // Generate SvelteKit routes if enabled
              if (svelteKit.enabled && manifest && server) {
                await generateSvelteKitRoutes(server.config.root, manifest, {
                  enabled: svelteKit.enabled,
                  routesDir: svelteKit.routesDir || 'src/routes/api',
                  objectsDir: svelteKit.objectsDir || 'src/lib/objects',
                  configPath: svelteKit.configPath || 'src/lib/server',
                  configFileName: svelteKit.configFileName || 'smrt.ts',
                  kebabRoutes: svelteKit.kebabRoutes ?? false,
                  knowledge: await resolveKnowledgeConfig(
                    server.config.root,
                    manifest,
                  ),
                });
              }
            }
          } catch (error) {
            console.error(
              `[smrt] Failed to rescan after removing ${file}:`,
              error,
            );
          }
        });
      }
    },

    resolveId(id) {
      // Resolve virtual module imports
      if (id in VIRTUAL_MODULES) {
        return `\0${VIRTUAL_MODULES[id as keyof typeof VIRTUAL_MODULES]}`;
      }

      // Resolve virtual index.html for dev UI (only in dev mode)
      if (id === '/index.html' && server) {
        return `\0smrt:index-html`;
      }

      return null;
    },

    async load(id) {
      // Load virtual modules (strip the \0 prefix)
      const cleanId = id.startsWith('\0') ? id.slice(1) : id;

      if (!manifest) {
        manifest = await scanAndGenerateManifest(projectRoot);
      }

      switch (cleanId) {
        case 'smrt:routes':
          // Routes module available in all modes
          return await generateRoutesModule(manifest);

        case 'smrt:client':
          // Client module available in both modes
          return generateClientModule(manifest, {
            kebabRoutes: svelteKit.kebabRoutes ?? false,
          });

        case 'smrt:mcp':
          // MCP module available in all modes
          return await generateMCPModule(manifest);

        case 'smrt:types':
          // Types module available in both modes
          return await generateTypesModule(manifest, pluginMode);

        case 'smrt:manifest':
          // Manifest module available in both modes
          return generateManifestModule(manifest);

        case 'smrt:schema':
          // Schema module available in both modes
          return await generateSchemaModule(manifest);

        case 'smrt:ui':
          // UI module for default development interface
          return await loadDefaultUI();

        case 'smrt:index-html':
          // Virtual index.html for projects without one
          return await loadDefaultHTML();

        case 'smrt:cli':
          // CLI module for command-line interface generation
          return await generateCLIModule(manifest);

        default:
          return null;
      }
    },

    transformIndexHtml: {
      order: 'pre',
      handler: async (html, _ctx) => {
        // Only provide default HTML if no index.html exists in project
        if (!server) return html;

        try {
          const { existsSync } = await import('node:fs');
          const { join } = await import('node:path');

          const projectRoot = server.config.root;
          const indexPath = join(projectRoot, 'index.html');

          // If index.html exists, use it as-is
          if (existsSync(indexPath)) {
            return html;
          }

          // Otherwise, provide default SMRT UI
          return await loadDefaultHTML();
        } catch (error) {
          console.error('[smrt] Error checking for index.html:', error);
          return html;
        }
      },
    },

    async closeBundle() {
      // Write manifest to disk during library builds
      // This allows published packages to include their manifest
      if (!manifest || !config?.build?.lib) {
        return;
      }

      try {
        const { writeFileSync, mkdirSync } = await import('node:fs');
        const { resolve, dirname } = await import('node:path');

        // Determine output directory.
        // `rollupOptions.output` may be a single object or an array; mirror the
        // prior runtime behavior where reading `.dir` off an array yields
        // undefined and falls through to `build.outDir`.
        const rollupOutput = config.build?.rollupOptions?.output;
        const rollupOutputDir = Array.isArray(rollupOutput)
          ? undefined
          : rollupOutput?.dir;
        const outDir = rollupOutputDir || config.build?.outDir || 'dist';
        const manifestPath = resolve(projectRoot, outDir, 'manifest.json');
        const knowledgePath = resolve(
          projectRoot,
          outDir,
          'smrt-knowledge.json',
        );

        // Ensure directory exists
        mkdirSync(dirname(manifestPath), { recursive: true });

        // Write manifest file
        writeFileSync(manifestPath, JSON.stringify(manifest, null, 2), 'utf-8');
        await writeDomainKnowledgeArtifact(
          manifest,
          projectRoot,
          knowledgePath,
          manifestPath,
        );

        const objectCount = Object.keys(manifest.objects).length;
        console.log(
          `[smrt] Wrote manifest with ${objectCount} objects to ${manifestPath}`,
        );
      } catch (error) {
        console.error('[smrt] Error writing manifest file:', error);
      }
    },
  };

  async function scanAndGenerateManifest(
    rootDir: string,
  ): Promise<SmartObjectManifest> {
    // NOTE: We do NOT use the framework's static manifest here
    // The static manifest JSON that core emits at build time
    // contains framework objects (like 'pleb') which should not be included
    // when scanning user code in consuming applications.
    //
    // smrtPlugin is designed to scan USER code dynamically, not load
    // framework objects. For consuming apps that need framework objects,
    // they should use smrtConsumer plugin instead.

    // Always use OXC-based scanner (consolidated in Issue #951)
    try {
      return await scanWithOxc(rootDir);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('Cannot find package')
      ) {
        console.error(
          '[smrt] @happyvertical/smrt-scanner is required but not installed.',
        );
        console.error(
          '[smrt] Install it: pnpm add @happyvertical/smrt-scanner',
        );
      }
      console.error('[smrt] Error scanning files:', error);
      // Re-throw to fail the build - don't silently continue with empty manifest
      throw error;
    }
  }

  /**
   * Scan files using OXC-based scanner (experimental fast path)
   * Uses Rust-based oxc-parser for 2-3x faster parsing
   */
  async function scanWithOxc(rootDir: string): Promise<SmartObjectManifest> {
    const startTime = performance.now();

    try {
      // Import the OXC scanner package
      const { OxcScanner, ManifestAdapter } = await importScanner();

      console.log(`[smrt] Using experimental OXC scanner for faster builds`);

      // Create scanner with options. Always exclude .svelte files as OXC barfs on <style> tags
      const safeExclude = [...(exclude || []), '**/*.svelte'];
      const scanner = new OxcScanner({
        cwd: rootDir,
        include,
        exclude: safeExclude,
      });

      // Scan and resolve inheritance
      const { results, resolved } = await scanner.scanAndResolve();

      // Check for errors
      if (results.errors.length > 0) {
        console.error(
          `\n[smrt] ❌ Build failed: ${results.errors.length} error(s) during scanning:\n`,
        );
        for (const error of results.errors) {
          console.error(
            `  ${error.filePath}:${error.line || '?'}: ${error.message}\n`,
          );
        }
        throw new Error(
          '[smrt] Build aborted due to scan errors. See above for details.',
        );
      }

      // Read package.json for metadata BEFORE creating manifest (Issue #713)
      // This ensures qualified names are generated correctly for namespace isolation
      // packageJson is also needed for agent manifest generation (component discovery)
      let packageName: string | undefined;
      let packageVersion: string | undefined;
      let packageJson: PackageJsonShape | undefined;
      try {
        const { readFileSync } = await import('node:fs');
        const { join } = await import('node:path');
        const pkgPath = join(rootDir, 'package.json');
        const pkgContent = readFileSync(pkgPath, 'utf-8');
        packageJson = JSON.parse(pkgContent) as PackageJsonShape;
        packageName = packageJson.name || undefined;
        packageVersion = packageJson.version || undefined;
      } catch {
        // package.json not found or invalid - continue without packageName
        console.warn(
          '[smrt] Warning: package.json not found. Qualified names will not be generated.',
        );
      }

      // Convert to manifest format with packageName for qualified names (Issue #713)
      const adapter = new ManifestAdapter();
      const newManifest = adapter.toManifest(resolved, {
        packageName,
        packageVersion,
        typeAliases: results.typeAliases,
      });

      // Add moduleType identifier
      newManifest.moduleType = 'smrt';

      // Keep this as a normal module import: dynamic source imports with a
      // `.js` specifier do not reliably resolve through tsx during publish-time
      // workspace builds, even though the compiled dist path exists.
      const smrtDependencies = discoverSmrtPackages({ baseDir: rootDir });
      if (smrtDependencies.length > 0) {
        newManifest.smrtDependencies = smrtDependencies;
        console.log(
          `[smrt] Found ${smrtDependencies.length} SMRT dependencies: ${smrtDependencies.join(', ')}`,
        );
      }

      // Run all manifest generation passes (same as TypeScript scanner path)
      const { ManifestGenerator } = await importBuildAwareModule<
        typeof import('../scanner/index.js')
      >({
        source: '../scanner/index.ts',
        dist: '../scanner.js',
      });
      const manifestGen = new ManifestGenerator();
      // IMPORTANT: keep this pass order aligned with generateManifest().
      manifestGen.normalizeReportTenantScope(newManifest);
      manifestGen.injectTenantScopedFields(newManifest);
      manifestGen.mergeInheritedFields(newManifest);
      manifestGen.normalizeReportObjects(newManifest);
      manifestGen.generateValidationRules(newManifest);
      manifestGen.generateSchemas(newManifest);
      manifestGen.assertTenantScopedSchemaContract(newManifest);
      // Final pass: Generate agent manifests for Agent subclasses
      // Derives permissions, features, menuItems, and components from code
      manifestGen.generateAgentManifests(newManifest, packageName, packageJson);

      const elapsed = performance.now() - startTime;

      // Log results
      const objectCount = Object.keys(newManifest.objects).length;
      if (objectCount > 0) {
        const names = Object.keys(newManifest.objects).join(', ');
        console.log(`[smrt] Found ${objectCount} SMRT objects: ${names}`);
      } else {
        console.log('[smrt] No SMRT objects found');
      }
      console.log(`[smrt] OXC scan completed in ${elapsed.toFixed(2)}ms`);

      // Generate TypeScript declarations if enabled
      if (generateTypes && server) {
        await generateTypeDeclarationFile(
          newManifest,
          rootDir,
          typeDeclarationsPath,
        );
      }

      return newManifest;
    } catch (error) {
      console.error('[smrt] Error in OXC scanner:', error);
      throw error;
    }
  }

  async function shouldRescan(file: string): Promise<boolean> {
    // Only rescan in server mode
    if (pluginMode === 'client') {
      return false;
    }

    try {
      // Conditionally import minimatch
      const { minimatch } = await import('minimatch');

      const isIncluded = include.some((pattern) => minimatch(file, pattern));
      const isExcluded = exclude.some((pattern) => minimatch(file, pattern));

      return isIncluded && !isExcluded;
    } catch (error) {
      console.warn('[smrt] Error checking file patterns:', error);
      return false;
    }
  }
}

/**
 * Generate virtual routes module
 */
async function generateRoutesModule(
  manifest: SmartObjectManifest,
): Promise<string> {
  try {
    const { ManifestGenerator } = await importBuildAwareModule<
      typeof import('../scanner/index.js')
    >({
      source: '../scanner/index.ts',
      dist: '../scanner.js',
    });
    const generator = new ManifestGenerator();
    const routes = generator.generateRestEndpoints(manifest);

    return `
// Auto-generated REST routes from SMRT objects
// This file is generated automatically - do not edit

/**
 * Setup routes on an Express-like app
 *
 * NOTE: This virtual module function is for documentation only.
 * For actual route registration, use:
 * - import { startRestServer } from '@happyvertical/smrt-core'
 * - Or APIGenerator from '@happyvertical/smrt-core/generators'
 *
 * @param app - Express-like app instance
 * @param options - Route configuration options
 * @param options.basePath - Base path for all routes (default: '/api/v1')
 */
export function setupRoutes(app, options = {}) {
  const basePath = options.basePath || '/api/v1';

  console.warn('[smrt] setupRoutes is a documentation function only.');
  console.warn('[smrt] Use startRestServer() or APIGenerator for actual route registration.');
  console.warn('[smrt] Available endpoints:');
${routes
  .split('\\n')
  .map((line) => `  console.warn(\`  \${basePath}${line.trim()}\`);`)
  .join('\\n')}
}

export { setupRoutes as default };
`;
  } catch (error) {
    console.warn('[smrt] Error generating routes module:', error);
    return 'export function setupRoutes(app, options = {}) { console.warn("Routes generation failed"); }';
  }
}

/**
 * Generate virtual client module
 */
function lowerFirst(value: string): string {
  return value ? value[0].toLowerCase() + value.slice(1) : value;
}

function uniqueApiClientEntries(
  objects: Array<[string, SmartObjectManifest['objects'][string]]>,
): Array<{
  objectName: string;
  obj: SmartObjectManifest['objects'][string];
  clientKey: string;
}> {
  const usedKeys = new Set<string>();

  return objects.map(([objectName, obj]) => {
    let clientKey = obj.collection;

    if (usedKeys.has(clientKey)) {
      clientKey = lowerFirst(obj.className);
    }

    const baseClientKey = clientKey;
    let suffix = 2;
    while (usedKeys.has(clientKey)) {
      clientKey = `${baseClientKey}${suffix}`;
      suffix += 1;
    }

    usedKeys.add(clientKey);
    return { objectName, obj, clientKey };
  });
}

function generateClientModule(
  manifest: SmartObjectManifest,
  options: { kebabRoutes?: boolean } = {},
): string {
  const objects = uniqueApiClientEntries(Object.entries(manifest.objects));

  const clientMethods = objects
    .map(({ obj, clientKey }) => {
      const { collection, methods = {} } = obj;

      // Honor api include/exclude + scope/static skip rules for custom methods,
      // so the client only emits proxies for methods that actually have routes
      // emitted by the server. (Custom only — CRUD is left unconditional to
      // preserve the existing client-type contract.)
      const exposedActions = resolveApiActionSet(obj);
      const apiConfig = obj.decoratorConfig?.api;
      const apiRoutes: Record<string, { path?: string }> =
        apiConfig && typeof apiConfig === 'object'
          ? ((apiConfig as { routes?: Record<string, { path?: string }> })
              .routes ?? {})
          : {};

      const customMethods = Object.entries(methods).filter(
        ([name, method]) => method.isPublic && exposedActions.has(name),
      );

      // Resolve the URL segment for each custom method using the same priority
      // the server does in normalizeCustomRoutePath: explicit api.routes[name].path
      // override wins; else kebab-case when kebabRoutes is enabled; else source casing.
      const segmentFor = (methodName: string): string => {
        const overridePath = apiRoutes[methodName]?.path;
        if (overridePath) {
          const trimmed = overridePath
            .split('/')
            .map((s) => s.trim())
            .filter(Boolean)
            .join('/');
          if (trimmed.length > 0) return trimmed;
        }
        return options.kebabRoutes ? methodNameToKebab(methodName) : methodName;
      };

      // Generate custom method implementations.
      // Custom methods are instance methods: POST /{collection}/{id}/{urlSegment}.
      const customMethodImpls = customMethods
        .map(([methodName, _method]) => {
          const urlSegment = segmentFor(methodName);
          return `    ${methodName}: (id, options) => fetch(basePath + '/${collection}/' + id + '/${urlSegment}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options || {})
    }).then(r => r.json())`;
        })
        .join(',\n');

      const customMethodsBlock =
        customMethods.length > 0 ? `,\n${customMethodImpls}` : '';

      return `
  ${clientKey}: {
    list: (params) => fetch(basePath + '/${collection}', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(r => r.json()),

    get: (id) => fetch(basePath + '/${collection}/' + id, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(r => r.json()),

    create: (data) => fetch(basePath + '/${collection}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(r => r.json()),

    update: (id, data) => fetch(basePath + '/${collection}/' + id, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(r => r.json()),

    delete: (id) => fetch(basePath + '/${collection}/' + id, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }).then(r => r.ok),

    search: (query) => fetch(basePath + '/${collection}/search?q=' + encodeURIComponent(query), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(r => r.json())${customMethodsBlock}
  }`;
    })
    .join(',');

  return `
// Auto-generated API client from SMRT objects
// This file is generated automatically - do not edit

export function createClient(basePath = '/api/v1') {
  return {${clientMethods}
  };
}

export { createClient as default };
`;
}

/**
 * Generate virtual MCP module
 */
async function generateMCPModule(
  manifest: SmartObjectManifest,
): Promise<string> {
  try {
    const { ManifestGenerator } = await importBuildAwareModule<
      typeof import('../scanner/index.js')
    >({
      source: '../scanner/index.ts',
      dist: '../scanner.js',
    });
    const generator = new ManifestGenerator();
    const tools = generator.generateMCPTools(manifest);

    return `
// Auto-generated MCP tools from SMRT objects
// This file is generated automatically - do not edit

import { SmrtMCPServer } from '@happyvertical/smrt-core/runtime';

export const tools = ${tools};

export function createMCPServer(options = {}) {
  const server = new SmrtMCPServer({
    name: options.name || 'smrt-auto-generated',
    version: options.version || '1.0.0',
    ...options
  });

  // Add all generated tools to the server
  for (const tool of tools) {
    server.addTool(tool, async (params) => {
      // Tool execution will be handled by the application
      throw new Error(\`Tool '\${tool.name}' handler must be provided by application\`);
    });
  }

  return server;
}

export { createMCPServer as default };
`;
  } catch (error) {
    console.warn('[smrt] Error generating MCP module:', error);
    return `
import { SmrtMCPServer } from '@happyvertical/smrt-core/runtime';
export const tools = [];
export function createMCPServer(options = {}) {
  console.warn("MCP generation failed");
  return new SmrtMCPServer({
    name: options.name || 'smrt-client',
    version: options.version || '1.0.0',
    ...options
  });
}
`;
  }
}

/**
 * Generate client-mode types without server dependencies
 */
function generateClientModeTypes(manifest: SmartObjectManifest): string {
  const typeDefinitions: string[] = [];

  // Generate interfaces for each object in the manifest
  for (const [objectName, objectMeta] of Object.entries(manifest.objects)) {
    const fields = objectMeta.fields || {};
    const propertyLines: string[] = [];

    for (const [fieldName, fieldDef] of Object.entries(fields)) {
      let type = 'any';

      // Map SMRT field types to TypeScript types
      switch (fieldDef.type) {
        case 'text':
          type = 'string';
          break;
        case 'decimal':
        case 'integer':
          type = 'number';
          break;
        case 'boolean':
          type = 'boolean';
          break;
        case 'datetime':
          type = 'string';
          break;
        case 'json':
          type = 'any';
          break;
        case 'foreignKey':
          type = 'string';
          break;
        default:
          type = 'any';
      }

      const optional = !fieldDef.required ? '?' : '';
      propertyLines.push(`  ${fieldName}${optional}: ${type};`);
    }

    // Add common SmrtObject properties
    propertyLines.unshift(
      '  id?: string;',
      '  created_at?: string;',
      '  updated_at?: string;',
    );

    const interfaceDef = `export interface ${objectName}Data {\n${propertyLines.join('\n')}\n}`;
    typeDefinitions.push(interfaceDef);
  }

  return typeDefinitions.join('\n\n');
}

/**
 * Generate virtual types module
 */
async function generateTypesModule(
  manifest: SmartObjectManifest,
  mode: 'server' | 'client' = 'server',
): Promise<string> {
  let interfaces = '';

  try {
    // Only use scanner in server mode to avoid Node.js dependencies in browser builds
    if (mode !== 'client') {
      const { ManifestGenerator } = await importBuildAwareModule<
        typeof import('../scanner/index.js')
      >({
        source: '../scanner/index.ts',
        dist: '../scanner.js',
      });
      const generator = new ManifestGenerator();
      interfaces = generator.generateTypeDefinitions(manifest);
    } else {
      // In client mode, generate basic interfaces directly from manifest
      interfaces = generateClientModeTypes(manifest);
    }

    return `
// Auto-generated TypeScript types from SMRT objects
// This file is generated automatically - do not edit

${interfaces}

export interface Request {
  params: Record<string, string>;
  query: Record<string, any>;
  json(): Promise<any>;
}

export interface Response {
  json(data: any, init?: { status?: number }): Response;
  status(code: number): Response;
}
`;
  } catch (error) {
    console.warn('[smrt] Error generating types module:', error);
    return `
// Auto-generated TypeScript types from SMRT objects (fallback)
// This file is generated automatically - do not edit

export interface Request {
  params: Record<string, string>;
  query: Record<string, any>;
  json(): Promise<any>;
}

export interface Response {
  json(data: any, init?: { status?: number }): Response;
  status(code: number): Response;
}
`;
  }
}

/**
 * Generate virtual manifest module
 */
function generateManifestModule(manifest: SmartObjectManifest): string {
  return `
// Auto-generated manifest from SMRT objects
// This file is generated automatically - do not edit

export const manifest = ${JSON.stringify(manifest, null, 2)};

export { manifest as default };
`;
}

/**
 * Generate TypeScript declaration file for virtual modules
 * This eliminates the need for manual type maintenance
 */
async function generateTypeDeclarationFile(
  manifest: SmartObjectManifest,
  projectRoot: string,
  typeDeclarationsPath: string,
): Promise<void> {
  try {
    // Conditionally import path and fs modules
    const [{ join }, { existsSync, mkdirSync, writeFileSync }] =
      await Promise.all([import('node:path'), import('node:fs')]);

    const declarationsDir = join(projectRoot, typeDeclarationsPath);
    const declarationsFile = join(declarationsDir, 'virtual-modules.d.ts');

    // Create directory if it doesn't exist
    if (!existsSync(declarationsDir)) {
      mkdirSync(declarationsDir, { recursive: true });
    }

    // Generate interface definitions for each discovered SMRT object
    const objectInterfaces = Object.entries(manifest.objects)
      .map(([_name, obj]) => {
        const interfaceName = `${obj.className}Data`;
        const fields = Object.entries(obj.fields)
          .map(([fieldName, field]) => {
            const optional = field.required === false ? '?' : '';
            const type = mapTypeScriptType(field.type);
            return `    ${fieldName}${optional}: ${type};`;
          })
          .join('\n');

        return `  export interface ${interfaceName} {
    id?: string;
${fields}
    createdAt?: string;
    updatedAt?: string;
  }`;
      })
      .join('\n\n');

    // Generate API client interface for each object
    // Always use plural collection names (standard REST convention).
    // Filter custom methods through resolveApiActionSet so the declared
    // type surface matches what generateClientModule actually emits at
    // runtime — otherwise consumers see methods in autocomplete that are
    // undefined at runtime.
    const apiClientInterface = uniqueApiClientEntries(
      Object.entries(manifest.objects),
    )
      .map(({ obj, clientKey }) => {
        const { className, methods = {} } = obj;
        const interfaceName = `${className}Data`;
        const exposedActions = resolveApiActionSet(obj);
        const customMethods = Object.entries(methods).filter(
          ([name, method]) => method.isPublic && exposedActions.has(name),
        );

        // Generate custom method signatures
        // Custom methods are instance methods requiring id as first parameter
        const customMethodSignatures = customMethods
          .map(([methodName, method]) => {
            const params = method.parameters || [];
            const optionsSignature =
              params.length > 0
                ? `, options?: { ${params.map((p) => `${p.name}?: ${mapTypeScriptType(p.type)}`).join('; ')} }`
                : '';
            return `      ${methodName}(id: string${optionsSignature}): Promise<any>;`;
          })
          .join('\n');

        if (customMethods.length > 0) {
          // Object with custom methods: include both CRUD and custom methods
          return `    ${clientKey}: CrudOperations<${interfaceName}> & {\n${customMethodSignatures}\n    };`;
        } else {
          // Standard CRUD operations only
          return `    ${clientKey}: CrudOperations<${interfaceName}>;`;
        }
      })
      .join('\n');

    // Generate MCP tool interfaces based on discovered methods
    const _mcpTools = Object.entries(manifest.objects).flatMap(([_name, obj]) =>
      Object.entries(obj.methods).map(([methodName, method]) => ({
        name: `${methodName}_${obj.collection}`,
        description: `${method.name} operation on ${obj.collection}`,
        inputSchema: {
          type: 'object',
          properties: Object.fromEntries(
            method.parameters.map((param) => [
              param.name,
              { type: mapJsonSchemaType(param.type) },
            ]),
          ),
          required: method.parameters
            .filter((p) => p.optional !== true)
            .map((p) => p.name),
        },
      })),
    );

    const typeDeclarations = `/**
 * Auto-generated TypeScript declarations for SMRT virtual modules
 * Generated from discovered @smrt() decorated classes
 * 
 * DO NOT EDIT THIS FILE MANUALLY
 * This file is automatically regenerated when SMRT objects change
 */

// Manifest module - Contains discovered SMRT objects metadata
declare module '@happyvertical/smrt-virt-manifest' {
  export interface SmrtObjectField {
    type: string;
    required?: boolean;
    default?: any;
  }

  export interface SmrtObjectMethod {
    name: string;
    parameters: Array<{
      name: string;
      type: string;
      required?: boolean;
    }>;
    returnType: string;
    isAsync: boolean;
  }

  export interface SmrtObjectDefinition {
    className: string;
    collection: string;
    fields: Record<string, SmrtObjectField>;
    methods: Record<string, SmrtObjectMethod>;
    decoratorConfig: any;
  }

  export interface SmrtManifest {
    version: string;
    timestamp: number;
    objects: Record<string, SmrtObjectDefinition>;
  }

  export const manifest: SmrtManifest;
}

// Routes module - Auto-generated REST route setup
declare module '@happyvertical/smrt-virt-routes' {
  export interface RouteApp {
    get(path: string, handler: (req: any, res: any) => void): void;
    post(path: string, handler: (req: any, res: any) => void): void;
    put(path: string, handler: (req: any, res: any) => void): void;
    delete(path: string, handler: (req: any, res: any) => void): void;
  }

  export interface RouteOptions {
    basePath?: string;
    [key: string]: any;
  }

  export function setupRoutes(app: RouteApp, options?: RouteOptions): void;
  export default setupRoutes;
}

// Client module - Auto-generated API client  
declare module '@happyvertical/smrt-virt-client' {
  export interface ApiResponse<T = any> {
    id?: string;
    data?: T;
    error?: string;
    message?: string;
  }

  export interface CrudOperations<T = any> {
    list(params?: Record<string, any>): Promise<T[]>;
    get(id: string): Promise<T>;
    create(data: Partial<T>): Promise<T>;
    update(id: string, data: Partial<T>): Promise<T>;
    delete(id: string): Promise<boolean>;
    search(query: string): Promise<T[]>;
  }

  export interface ApiClient {
${apiClientInterface}
  }

  export function createClient(basePath?: string): ApiClient;
  export default createClient;
}

// MCP module - Auto-generated Model Context Protocol tools
declare module '@happyvertical/smrt-virt-mcp' {
  import type { SmrtMCPServer, MCPServerOptions } from '@happyvertical/smrt-core/runtime';

  export interface McpTool {
    name: string;
    description: string;
    inputSchema: {
      type: string;
      properties: Record<string, any>;
      required?: string[];
    };
  }

  export const tools: McpTool[];
  export function createMCPServer(options?: MCPServerOptions): SmrtMCPServer;
  export default createMCPServer;
}

// Types module - Auto-generated TypeScript interfaces
declare module '@happyvertical/smrt-virt-types' {
  export const types: string;

  // Auto-generated interfaces for discovered SMRT objects
${objectInterfaces}

  export default types;
}

// CLI module - Auto-generated command-line interface
declare module '@happyvertical/smrt-virt-cli' {
  export interface CLIConfig {
    name?: string;
    version?: string;
    description?: string;
    prompt?: boolean;
    colors?: boolean;
  }

  export interface CLIContext {
    db?: any;
    ai?: any;
    user?: {
      id: string;
      roles?: string[];
    };
  }

  export interface CLICommandMap {
    [objectName: string]: {
      collection: string;
      commands: string[];
    };
  }

  export const cliCommands: CLICommandMap;

  export function setupCLI(config?: CLIConfig, context?: CLIContext): {
    run: (argv: string[]) => Promise<void>;
    generator: any;
  };

  export function getCLIHandler(config?: CLIConfig, context?: CLIContext): (argv: string[]) => Promise<void>;

  export default setupCLI;
}`;

    // Write the declarations file
    writeFileSync(declarationsFile, typeDeclarations);
    console.log(
      `[smrt] Generated TypeScript declarations: ${declarationsFile}`,
    );
  } catch (error) {
    console.error('[smrt] Error generating TypeScript declarations:', error);
  }
}

/**
 * Map SMRT field types to TypeScript types
 */
function mapTypeScriptType(smrtType: string): string {
  const typeMap: Record<string, string> = {
    string: 'string',
    number: 'number',
    boolean: 'boolean',
    array: 'any[]',
    object: 'Record<string, any>',
    date: 'string',
    Date: 'string',
  };
  return typeMap[smrtType] || 'any';
}

/**
 * Map TypeScript types to JSON Schema types for MCP tools
 */
function mapJsonSchemaType(tsType: string): string {
  const typeMap: Record<string, string> = {
    string: 'string',
    number: 'number',
    boolean: 'boolean',
    array: 'array',
    object: 'object',
    any: 'string',
  };
  return typeMap[tsType] || 'string';
}

/**
 * Get default HTML template with inlined JavaScript (inlined for distribution)
 */
function getDefaultHTML(): string {
  const uiScript = getDefaultUIModule();

  // Build HTML without template literals to avoid escaping issues
  return (
    '<!DOCTYPE html>\n' +
    '<html lang="en">\n' +
    '<head>\n' +
    '  <meta charset="UTF-8">\n' +
    '  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n' +
    '  <title>SMRT Development UI</title>\n' +
    '  <style>\n' +
    '    * { margin: 0; padding: 0; box-sizing: border-box; }\n' +
    '    body {\n' +
    '      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;\n' +
    '      background: #f5f5f5;\n' +
    '      color: #333;\n' +
    '    }\n' +
    '    .container { max-width: 1200px; margin: 0 auto; padding: 20px; }\n' +
    '    header {\n' +
    '      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);\n' +
    '      color: white;\n' +
    '      padding: 40px 0;\n' +
    '      margin-bottom: 40px;\n' +
    '      box-shadow: 0 4px 6px rgba(0,0,0,0.1);\n' +
    '    }\n' +
    '    header h1 { font-size: 2.5em; font-weight: 700; margin-bottom: 10px; }\n' +
    '    .subtitle { font-size: 1.1em; opacity: 0.9; }\n' +
    '    .stats {\n' +
    '      display: grid;\n' +
    '      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));\n' +
    '      gap: 20px;\n' +
    '      margin-bottom: 40px;\n' +
    '    }\n' +
    '    .stat-card {\n' +
    '      background: white;\n' +
    '      padding: 20px;\n' +
    '      border-radius: 8px;\n' +
    '      box-shadow: 0 2px 4px rgba(0,0,0,0.1);\n' +
    '      text-align: center;\n' +
    '    }\n' +
    '    .stat-value { font-size: 2.5em; font-weight: 700; color: #667eea; margin-bottom: 5px; }\n' +
    '    .stat-label { font-size: 0.9em; color: #666; text-transform: uppercase; letter-spacing: 1px; }\n' +
    '    .collections { display: grid; gap: 30px; }\n' +
    '    .collection-card {\n' +
    '      background: white;\n' +
    '      border-radius: 8px;\n' +
    '      box-shadow: 0 2px 4px rgba(0,0,0,0.1);\n' +
    '      overflow: hidden;\n' +
    '    }\n' +
    '    .collection-header {\n' +
    '      background: #667eea;\n' +
    '      color: white;\n' +
    '      padding: 15px 20px;\n' +
    '      display: flex;\n' +
    '      justify-content: space-between;\n' +
    '      align-items: center;\n' +
    '    }\n' +
    '    .collection-title { font-size: 1.3em; font-weight: 600; }\n' +
    '    .collection-count {\n' +
    '      background: rgba(255,255,255,0.2);\n' +
    '      padding: 5px 15px;\n' +
    '      border-radius: 20px;\n' +
    '      font-size: 0.9em;\n' +
    '    }\n' +
    '    .collection-body { padding: 20px; }\n' +
    '    .field-list {\n' +
    '      display: grid;\n' +
    '      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));\n' +
    '      gap: 15px;\n' +
    '      margin-bottom: 20px;\n' +
    '    }\n' +
    '    .field {\n' +
    '      padding: 10px;\n' +
    '      background: #f8f9fa;\n' +
    '      border-radius: 4px;\n' +
    '      border-left: 3px solid #667eea;\n' +
    '    }\n' +
    '    .field-name { font-weight: 600; color: #333; margin-bottom: 3px; }\n' +
    '    .field-type { font-size: 0.85em; color: #666; }\n' +
    '    .actions { display: flex; gap: 10px; flex-wrap: wrap; }\n' +
    '    .btn {\n' +
    '      padding: 10px 20px;\n' +
    '      border: none;\n' +
    '      border-radius: 4px;\n' +
    '      font-size: 0.9em;\n' +
    '      cursor: pointer;\n' +
    '      transition: all 0.2s;\n' +
    '      text-decoration: none;\n' +
    '      display: inline-block;\n' +
    '    }\n' +
    '    .btn-primary { background: #667eea; color: white; }\n' +
    '    .btn-primary:hover { background: #5568d3; transform: translateY(-1px); }\n' +
    '    .btn-secondary { background: #e0e0e0; color: #333; }\n' +
    '    .btn-secondary:hover { background: #d0d0d0; }\n' +
    '    .loading { text-align: center; padding: 40px; color: #666; }\n' +
    '    .error {\n' +
    '      background: #fee;\n' +
    '      border: 1px solid #fcc;\n' +
    '      color: #c33;\n' +
    '      padding: 15px;\n' +
    '      border-radius: 4px;\n' +
    '      margin-bottom: 20px;\n' +
    '    }\n' +
    '    .empty-state { text-align: center; padding: 60px 20px; color: #999; }\n' +
    '    .empty-state svg { width: 100px; height: 100px; margin-bottom: 20px; opacity: 0.3; }\n' +
    '  </style>\n' +
    '</head>\n' +
    '<body>\n' +
    '  <div id="app">\n' +
    '    <div class="loading">Loading SMRT UI...</div>\n' +
    '  </div>\n' +
    '  <script type="module">\n' +
    uiScript +
    '\n  </script>\n' +
    '</body>\n' +
    '</html>'
  );
}

/**
 * Get default UI module from template file
 * Loads the JavaScript template
 */
function getDefaultUIModule(): string {
  try {
    // Get current file directory
    const __dirname = dirname(fileURLToPath(import.meta.url));

    // Load template file (works in both src and dist)
    const templatePath = join(__dirname, 'templates/default-ui.js');

    return readFileSync(templatePath, 'utf-8').trim();
  } catch (error) {
    console.error('[smrt] Error loading UI template:', error);
    // Fallback to minimal UI
    return `
async function createUI() {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = '<div class="container" style="padding:40px;text-align:center;"><h1>🎯 SMRT Development UI</h1><p>Template file not found. UI code could not be loaded.</p></div>';
}
createUI();
`;
  }
}

/**
 * Load default HTML template for projects without index.html
 */
async function loadDefaultHTML(): Promise<string> {
  return getDefaultHTML();
}

/**
 * Load default UI module
 */
async function loadDefaultUI(): Promise<string> {
  return getDefaultUIModule();
}

/**
 * Generate virtual CLI module
 */
async function generateCLIModule(
  manifest: SmartObjectManifest,
): Promise<string> {
  try {
    // Import CLI types
    const commands: string[] = [];
    const objectImports: string[] = [];

    // Generate CLI setup code for each object
    for (const [className, objectDef] of Object.entries(manifest.objects)) {
      const config = objectDef.decoratorConfig;
      const cliConfig = config?.cli;

      // Skip if CLI is disabled
      if (cliConfig === false) continue;

      // Determine which operations to include
      const excluded =
        (typeof cliConfig === 'object' ? cliConfig.exclude : []) || [];
      const included = typeof cliConfig === 'object' ? cliConfig.include : null;

      const shouldInclude = (command: string) => {
        if (included && !included.includes(command)) return false;
        if (excluded.includes(command)) return false;
        return true;
      };

      // Collection name (metadata) and the command segment. Commands are keyed
      // by the simple class name (e.g. `document:list`) to match what
      // `CLIGenerator` resolves and the dev `smrt` CLI convention — NOT the
      // plural collection name, which the runtime resolver does not accept.
      const collectionName = objectDef.collection;
      const commandName = (objectDef.className || className).toLowerCase();

      // Generate import statement for the object class
      objectImports.push(
        `// Import ${className} and ${className}Collection for CLI operations`,
      );

      // Generate command registration
      const availableCommands: string[] = [];

      // Standard CRUD commands
      if (shouldInclude('list'))
        availableCommands.push(`'${commandName}:list'`);
      if (shouldInclude('get')) availableCommands.push(`'${commandName}:get'`);
      if (shouldInclude('create'))
        availableCommands.push(`'${commandName}:create'`);
      if (shouldInclude('update'))
        availableCommands.push(`'${commandName}:update'`);
      if (shouldInclude('delete'))
        availableCommands.push(`'${commandName}:delete'`);

      // Custom action methods
      for (const [methodName, _method] of Object.entries(objectDef.methods)) {
        // Skip private methods and standard CRUD
        if (
          methodName.startsWith('_') ||
          ['list', 'get', 'create', 'update', 'delete', 'save'].includes(
            methodName,
          )
        )
          continue;

        if (shouldInclude(methodName)) {
          availableCommands.push(`'${commandName}:${methodName}'`);
        }
      }

      if (availableCommands.length > 0) {
        commands.push(`
  // ${className} commands
  ${className}: {
    collection: '${collectionName}',
    commands: [${availableCommands.join(', ')}]
  }`);
      }
    }

    return `
// Auto-generated CLI module from SMRT objects
// This file is generated automatically - do not edit

import { CLIGenerator } from '@happyvertical/smrt-core/generators/cli';

/**
 * @typedef {import('@happyvertical/smrt-core/generators/cli').CLIConfig} CLIConfig
 * @typedef {import('@happyvertical/smrt-core/generators/cli').CLIContext} CLIContext
 */

${objectImports.join('\n')}

/**
 * Available CLI commands by object
 */
export const cliCommands = {${commands.join(',\n')}
};

/**
 * Setup CLI with auto-generated commands
 *
 * @param {CLIConfig} [config={}] - CLI configuration
 * @param {CLIContext} [context={}] - CLI context
 * @returns {{run: function(string[]): Promise<void>, generator: *}}
 *
 * @example
 * import { setupCLI } from '@happyvertical/smrt-virt-cli';
 *
 * const cli = setupCLI({
 *   name: 'my-app',
 *   version: '1.0.0'
 * });
 *
 * cli.run(process.argv);
 */
export function setupCLI(config = {}, context = {}) {
  const generator = new CLIGenerator(config, context);
  return {
    run: async (argv) => {
      const handler = generator.generateHandler();
      await handler(argv.slice(2)); // Remove 'node' and script name
    },
    generator
  };
}

/**
 * Get CLI handler directly
 *
 * @param {CLIConfig} [config={}] - CLI configuration
 * @param {CLIContext} [context={}] - CLI context
 * @returns {function(string[]): Promise<void>}
 */
export function getCLIHandler(config = {}, context = {}) {
  const generator = new CLIGenerator(config, context);
  return generator.generateHandler();
}

export default setupCLI;
`;
  } catch (error) {
    console.warn('[smrt] Error generating CLI module:', error);
    return `
// Error generating CLI module
export const cliCommands = {};
export function setupCLI() {
  console.warn("CLI generation failed");
  return { run: async () => {} };
}
export function getCLIHandler() {
  return async () => console.warn("CLI generation failed");
}
export default setupCLI;
`;
  }
}

/**
 * Generate virtual schema module with JSON manifests
 */
async function generateSchemaModule(
  manifest: SmartObjectManifest,
): Promise<string> {
  try {
    const { SchemaGenerator } = await importBuildAwareModule<
      typeof import('../schema/generator.js')
    >({
      source: '../schema/generator.ts',
      dist: '../schema/generator.js',
    });

    const schemaGenerator = new SchemaGenerator();
    const schemas: Record<string, SchemaDefinition> = {};

    // Generate schemas for all SMRT objects
    for (const [className, objectDef] of Object.entries(manifest.objects)) {
      const schema = schemaGenerator.generateSchema(objectDef);
      schemas[className] = schema;
    }

    // Create JSON manifest for schemas
    const schemaManifest = {
      version: '1.0.0',
      timestamp: Date.now(),
      packageName: manifest.packageName || 'unknown',
      schemas: schemas,
      dependencies: Array.from(
        new Set(Object.values(schemas).flatMap((s) => s.dependencies || [])),
      ),
    };

    return `// Auto-generated schema manifest from SMRT objects
// This file is generated automatically - do not edit

// Schema manifest as JSON for SQL adapters
export const schemaManifest = ${JSON.stringify(schemaManifest, null, 2)};

// Schema registry for runtime access
export const schemas = schemaManifest.schemas;

// Schema lookup function
export function getSchema(className: string) {
  return schemas[className];
}

// All schemas as array for dependency resolution
export const allSchemas = Object.values(schemas);

// Package information
export const packageName = schemaManifest.packageName;
export const dependencies = schemaManifest.dependencies;

export default schemaManifest;`;
  } catch (error) {
    console.error('[smrt] Error generating schema module:', error);
    return `// Error generating schema module
export const schemaManifest = { schemas: {}, dependencies: [] };
export const schemas = {};
export function getSchema() { return null; }
export const allSchemas = [];
export default {};`;
  }
}
